import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Form, Input, Modal, Popconfirm, Space, Table, Tooltip, Upload } from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { addComplianceManual, deleteComplianceManual, getComplianceManuals, getPreSignedUrl, updateComplianceManual } from "./educationApi";
import { convertFileToBase64 } from "./fileUtils";
import { UPLOAD_RULES, uploadHint, uploadProps } from "utils/uploadRules";

const manualUpload = uploadProps(UPLOAD_RULES.MANUAL);
const listOf = ({ error, result }) => (!error && result?.success !== false && Array.isArray(result?.result) ? result.result : []);

// Employee Connect > Compliance Manual: the manuals (e.g. Cleaning, Laundry,
// OHS/WHS) the client's staff open in the app. Each has a name and a PDF.
const ComplianceManual = () => {
  const [form] = Form.useForm();
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ visible: false, record: null });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setManuals(listOf(await getComplianceManuals()));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = (record = null) => {
    setFile(null);
    setModal({ visible: true, record });
    form.resetFields();
    if (record) form.setFieldsValue({ name: record.name });
  };
  const closeModal = () => setModal({ visible: false, record: null });

  const save = async (values) => {
    const editing = modal.record;
    if (!editing && !file) return toast.error("Please upload the manual PDF.");
    setSaving(true);
    const body = { name: values.name };
    if (file) {
      body.file = await convertFileToBase64(file);
      body.filename = file.name;
    }
    const { error, result } = editing ? await updateComplianceManual(editing.id, body) : await addComplianceManual(body);
    setSaving(false);
    if (!error && result?.success) {
      toast.success(editing ? "Manual updated successfully." : "Manual added successfully.");
      closeModal();
      load();
    } else {
      toast.error(result?.message || "The manual could not be saved. Please try again.");
    }
  };

  const remove = async (record) => {
    const { error, result } = await deleteComplianceManual(record.id);
    if (!error && result?.success) {
      toast.success("Manual deleted successfully.");
      load();
    } else {
      toast.error(result?.message || "The manual could not be deleted. Please try again.");
    }
  };

  const view = async (record) => {
    const { error, result } = await getPreSignedUrl(record.pdf_url);
    if (!error && result?.result) window.open(result.result, "_blank");
    else toast.error("The manual could not be opened.");
  };

  const columns = [
    { title: "Manual", dataIndex: "name", key: "name" },
    { title: "Last Updated", dataIndex: "updated_date", key: "updated_date", width: 160 },
    {
      title: "Action",
      key: "action",
      width: 180,
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button icon={<EyeOutlined />} aria-label="View" onClick={() => view(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button icon={<EditOutlined />} aria-label="Edit" onClick={() => openModal(record)} />
          </Tooltip>
          <Popconfirm title="Are you sure you want to delete this manual?" okText="Delete" okType="danger" onConfirm={() => remove(record)}>
            <Tooltip title="Delete">
              <Button danger icon={<DeleteOutlined />} aria-label="Delete" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card bodyStyle={{ padding: "24px 28px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div style={{ color: "#5B6472", fontSize: 13 }}>The manuals your staff can open in the app.</div>
        <Button type="primary" shape="round" icon={<PlusCircleOutlined />} onClick={() => openModal()}>
          Add Manual
        </Button>
      </div>

      <Table rowKey="id" columns={columns} dataSource={manuals} loading={loading} pagination={false} locale={{ emptyText: "No manuals yet." }} />

      <Modal title={modal.record ? "Edit Manual" : "Add Manual"} visible={modal.visible} onCancel={closeModal} footer={null} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={save}>
          <Form.Item name="name" label="Manual Name" rules={[{ required: true, whitespace: true, message: "Please enter the manual name." }]}>
            <Input placeholder="e.g. Cleaning" maxLength={100} />
          </Form.Item>
          <Form.Item
            label="PDF"
            required={!modal.record}
            extra={
              <>
                {uploadHint(UPLOAD_RULES.MANUAL)}
                {modal.record && <div>Leave empty to keep the current PDF.</div>}
              </>
            }
          >
            <Upload
              accept={manualUpload.accept}
              maxCount={1}
              beforeUpload={(chosen) => {
                const result = manualUpload.beforeUpload(chosen);
                if (result === false) setFile(chosen);
                return result;
              }}
              onRemove={() => setFile(null)}
            >
              <Button icon={<UploadOutlined />}>{file ? "Change PDF" : "Select PDF"}</Button>
            </Upload>
          </Form.Item>
          <div style={{ textAlign: "center" }}>
            <Button type="primary" htmlType="submit" loading={saving}>
              {modal.record ? "Update" : "Add"}
            </Button>
          </div>
        </Form>
      </Modal>
    </Card>
  );
};

export default ComplianceManual;
